import React, { useReducer, useContext, type ReactNode } from 'react';
import axios from 'axios';

import { AuthState } from 'services/auth';
import { API_URL } from 'consts';
import type {
  MonthlyReportRow,
  ProizvodnjaAggregate,
  ReportSortMode,
  WorkEntryPayload,
  WorkTypeReportResponse,
} from 'types/api';

interface ReportStateShape {
  loading: boolean;
  error: unknown;
  proizvodnjaNorma: Record<string, ProizvodnjaAggregate | unknown>;
  proizvodnjaOtpremnica: Record<string, ProizvodnjaAggregate | unknown>;
  [key: string]: unknown;
}

interface ProizvodnjaRequest {
  dataId: string;
  ids: number[];
  startDate: string;
  endDate: string;
}

interface ReportContextValue extends ReportStateShape {
  fetchMonth: (month: string, sortMode?: ReportSortMode) => Promise<void>;
  fetchWorker: (workerId: string, startDate: string, endDate: string) => Promise<void>;
  fetchWork: (
    workType: string,
    workTypeId: string | number,
    startDate: string,
    endDate: string,
  ) => Promise<void>;
  fetchProizvodnjaNorma: (args: ProizvodnjaRequest) => Promise<void>;
  fetchProizvodnjaOtpremnica: (args: ProizvodnjaRequest) => Promise<void>;
}

type ReportAction =
  | { type: 'FETCH_MONTH_START' }
  | { type: 'FETCH_WORKER_START' }
  | { type: 'FETCH_WORK_START' }
  | { type: 'FETCH_PROIZVODNJA_NORMA_START' }
  | { type: 'FETCH_PROIZVODNJA_OTPREMNICA_START' }
  | { type: 'FETCH_MONTH_SUCCESS'; month: string; payload: MonthlyReportRow[] }
  | { type: 'FETCH_WORKER_SUCCESS'; workerId: string; payload: WorkEntryPayload[] }
  | {
      type: 'FETCH_WORK_SUCCESS';
      workType: string;
      workTypeId: string | number;
      payload: WorkTypeReportResponse;
    }
  | { type: 'FETCH_PROIZVODNJA_NORMA_SUCCESS'; dataId: string; payload: ProizvodnjaAggregate }
  | { type: 'FETCH_PROIZVODNJA_OTPREMNICA_SUCCESS'; dataId: string; payload: ProizvodnjaAggregate }
  | { type: 'FETCH_MONTH_FAIL'; error: unknown }
  | { type: 'FETCH_WORKER_FAIL'; error: unknown }
  | { type: 'FETCH_WORK_FAIL'; error: unknown }
  | { type: 'FETCH_PROIZVODNJA_NORMA_FAIL'; dataId: string; error: unknown }
  | { type: 'FETCH_PROIZVODNJA_OTPREMNICA_FAIL'; dataId: string; error: unknown };

const initialState: ReportStateShape = {
  loading: false,
  error: null,
  proizvodnjaNorma: {},
  proizvodnjaOtpremnica: {},
};

const noopAsync = async () => {};

export const ReportState = React.createContext<ReportContextValue>({
  ...initialState,
  fetchMonth: noopAsync,
  fetchWorker: noopAsync,
  fetchWork: noopAsync,
  fetchProizvodnjaNorma: noopAsync,
  fetchProizvodnjaOtpremnica: noopAsync,
});

const reducer = (state: ReportStateShape, action: ReportAction): ReportStateShape => {
  switch (action.type) {
    case 'FETCH_MONTH_START':
    case 'FETCH_WORKER_START':
    case 'FETCH_WORK_START':
    case 'FETCH_PROIZVODNJA_NORMA_START':
    case 'FETCH_PROIZVODNJA_OTPREMNICA_START':
      return { ...state, loading: true, error: null };
    case 'FETCH_MONTH_SUCCESS':
      return { ...state, loading: false, [action.month]: action.payload };
    case 'FETCH_WORKER_SUCCESS':
      return { ...state, loading: false, [action.workerId]: action.payload };
    case 'FETCH_WORK_SUCCESS': {
      const ctxKey = `${action.workType}-${action.workTypeId}`;
      return { ...state, loading: false, [ctxKey]: action.payload };
    }
    case 'FETCH_PROIZVODNJA_NORMA_SUCCESS':
      return {
        ...state,
        loading: false,
        proizvodnjaNorma: { ...state.proizvodnjaNorma, [action.dataId]: action.payload },
      };
    case 'FETCH_PROIZVODNJA_OTPREMNICA_SUCCESS':
      return {
        ...state,
        loading: false,
        proizvodnjaOtpremnica: { ...state.proizvodnjaOtpremnica, [action.dataId]: action.payload },
      };
    case 'FETCH_MONTH_FAIL':
    case 'FETCH_WORKER_FAIL':
    case 'FETCH_WORK_FAIL':
      return { ...state, loading: false, error: action.error };
    case 'FETCH_PROIZVODNJA_NORMA_FAIL':
      return {
        ...state,
        loading: false,
        proizvodnjaNorma: { ...state.proizvodnjaNorma, [action.dataId]: action.error },
      };
    case 'FETCH_PROIZVODNJA_OTPREMNICA_FAIL':
      return {
        ...state,
        loading: false,
        proizvodnjaOtpremnica: { ...state.proizvodnjaOtpremnica, [action.dataId]: action.error },
      };
    default:
      return state;
  }
};

export const ReportProvider = ({ children }: { children: ReactNode }) => {
  const { access_token } = useContext(AuthState);
  const [state, dispatch] = useReducer(reducer, initialState);

  const fetchMonth = async (month: string, sortMode: ReportSortMode = 'name') => {
    dispatch({ type: 'FETCH_MONTH_START' });
    const uri = `report/month/${month}?sortMode=${sortMode}`;

    try {
      const res = await axios.get<MonthlyReportRow[]>(`${API_URL}/${uri}`, {
        headers: {
          authorization: `Bearer: ${access_token}`,
        },
      });

      dispatch({ type: 'FETCH_MONTH_SUCCESS', month, payload: res.data });
    } catch (error) {
      dispatch({ type: 'FETCH_MONTH_FAIL', error });
    }
  };

  const fetchWorker = async (workerId: string, startDate: string, endDate: string) => {
    dispatch({ type: 'FETCH_WORKER_START' });
    const uri = `report/worker/${workerId}?startDate=${startDate}&endDate=${endDate}`;

    try {
      const res = await axios.get<WorkEntryPayload[]>(`${API_URL}/${uri}`, {
        headers: {
          authorization: `Bearer: ${access_token}`,
        },
      });

      dispatch({ type: 'FETCH_WORKER_SUCCESS', workerId, payload: res.data });
    } catch (error) {
      dispatch({ type: 'FETCH_WORKER_FAIL', error });
    }
  };

  const fetchWork = async (
    workType: string,
    workTypeId: string | number,
    startDate: string,
    endDate: string,
  ) => {
    dispatch({ type: 'FETCH_WORK_START' });
    const uri = `report/${workType}/${workTypeId}/?startDate=${startDate}&endDate=${endDate}`;

    try {
      const res = await axios.get<WorkTypeReportResponse>(`${API_URL}/${uri}`, {
        headers: {
          authorization: `Bearer: ${access_token}`,
        },
      });

      dispatch({ type: 'FETCH_WORK_SUCCESS', workType, workTypeId, payload: res.data });
    } catch (error) {
      dispatch({ type: 'FETCH_WORK_FAIL', error });
    }
  };

  const fetchProizvodnjaNorma = async ({ dataId, ids, startDate, endDate }: ProizvodnjaRequest) => {
    dispatch({ type: 'FETCH_PROIZVODNJA_NORMA_START' });

    try {
      const res = await axios.post<ProizvodnjaAggregate>(
        `${API_URL}/report/proizvodnja/norma`,
        { ids, startDate, endDate },
        {
          headers: {
            authorization: `Bearer: ${access_token}`,
          },
        },
      );

      dispatch({ type: 'FETCH_PROIZVODNJA_NORMA_SUCCESS', dataId, payload: res.data });
    } catch (error) {
      dispatch({ type: 'FETCH_PROIZVODNJA_NORMA_FAIL', dataId, error });
    }
  };

  const fetchProizvodnjaOtpremnica = async ({
    dataId,
    ids,
    startDate,
    endDate,
  }: ProizvodnjaRequest) => {
    dispatch({ type: 'FETCH_PROIZVODNJA_OTPREMNICA_START' });

    try {
      const res = await axios.post<ProizvodnjaAggregate>(
        `${API_URL}/report/proizvodnja/otpremnica`,
        { ids, startDate, endDate },
        {
          headers: {
            authorization: `Bearer: ${access_token}`,
          },
        },
      );

      dispatch({ type: 'FETCH_PROIZVODNJA_OTPREMNICA_SUCCESS', dataId, payload: res.data });
    } catch (error) {
      dispatch({ type: 'FETCH_PROIZVODNJA_OTPREMNICA_FAIL', dataId, error });
    }
  };

  const value: ReportContextValue = {
    ...state,
    fetchMonth,
    fetchWorker,
    fetchWork,
    fetchProizvodnjaNorma,
    fetchProizvodnjaOtpremnica,
  };

  return <ReportState.Provider value={value}>{children}</ReportState.Provider>;
};
